
import { Component, Vue, Watch, Prop } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import vselect3 from "vselect3";

// Types
import { UserPermissions } from "@/store/types";
import {
	UserGroup,
} from "@/store/user-management/types";
import { ServerEventType, LoadServerEventTypesParams } from "@/store/eventqueue/types";

// SV components
import GenericTable, {
	TableHeader
} from "@/components/table/generic-table.vue";
import { ModalItem } from "@/components/table/generic-update-modal.vue";
import UserGroupRoles from "./UserGroupRoles.vue";
import UserGroupDomains from "./UserGroupDomains.vue";

// VueX namespaces
const Eventqueue = namespace("eventqueue");
const UserManagement = namespace("userManagement");
const EventQueue = namespace("eventqueue");

import api from "@/services/api.service";

// Utility extensions
import "@/scripts/array-operations";
import { ServerType } from '@/constants/ServerType';

const serverTypeId_ManualRaise: number = 435;

@Component({
	components: {
		"generic-table": GenericTable,
		"v-select3": vselect3,
	}
})
export default class UserGroupSetup extends Vue {
	@Getter getFeature: (featureName: string[]) => boolean;
	@Getter("getPermissions") private permissions: UserPermissions;

	@UserManagement.Action public loadUserDomains: () => void;

	@Eventqueue.Action public sitesTreeGet: any;
	@EventQueue.Action loadServerEventTypes: (params: LoadServerEventTypesParams) => Promise<void>;
	@EventQueue.Getter("serverEventTypes") eventTypes: ServerEventType[];

	@Prop()
	public selectedTenantGroupId: number;

	public groupsList: UserGroup[] = [];
	private duressServerEventTypes: ServerEventType[] = [];

	private isLoading: boolean = false;
	private dataLoaded: boolean = false;

	public groupColumns: TableHeader[] = [
		{
			title: "Group Name",
			key: "title",
			order: 1,
			sortOrder: 0,
			sortOrderReversed: false,
			description: "The group name",
			searchable: true,
			visible: true,
			dataType: "input"
		},
		{
			title: "Roles",
			key: "roles",
			order: 2,
			sortOrder: 0,
			sortOrderReversed: false,
			description: "Group roles",
			searchable: false,
			visible: true,
			dataType: "list",
			useCustomCell: true
		}
	];

	public groupModalItems: ModalItem[] = [
		{
			title: "Group Name",
			key: "title",
			dataType: "text",
			required: true
		} as ModalItem,
		{
			title: "Roles",
			key: "roles",
			dataType: "component",
			data: UserGroupRoles,
			defaultValue: [],
			props: {
				selectedTenantGroupId: this.selectedTenantGroupId
			}
		}
	];

	public async mounted() {
		
	}

	public async updated() {
		if (this.selectedTenantGroupId === undefined) {
			return;
		}

		if (this.dataLoaded || this.isLoading) {
			return;
		}

		this.isLoading = true;

		let domainsLoaded = false;
		if (this.getFeature(['UserGroups']) && this.getFeature(['Domains'])) {
			await this.loadUserDomains();

			this.groupColumns.push({
				title: "Domain Groups",
				key: "domainGroups",
				order: 3,
				sortOrder: 0,
				sortOrderReversed: false,
				description: "Auto-add from domain groups",
				searchable: false,
				visible: true,
				dataType: "list",
				useCustomCell: true
			});

			this.groupModalItems.push({
				title: "Domain Groups",
				key: "domainGroups",
				dataType: "component",
				data: UserGroupDomains,
				defaultValue: []
			});

			domainsLoaded = true;
		}

		if (this.selectedTenantGroupId) {
			this.groupsList = await api.getUserGroups(this.selectedTenantGroupId);
		
			if (this.getFeature(['Mobile', 'Duress']) && 
				this.permissions.canSetDuressEventType !== undefined && 
				this.permissions.canSetDuressEventType === true) {
				await this.SetupUserGroupEventDuressTypeConfiguration(domainsLoaded);
			}
		}

		await this.sitesTreeGet();

		this.dataLoaded = true;
		this.isLoading = false;
	}

	public async updateUserGroup(userGroup: UserGroup) {
		if (userGroup) {
			await api.saveUserGroup(userGroup, this.selectedTenantGroupId);
			this.groupsList = await api.getUserGroups(this.selectedTenantGroupId);
		}
	}

	public async deleteUserGroup(deleteGroup: UserGroup) {
		await api.deleteUserGroup(deleteGroup.userGroupId, this.selectedTenantGroupId);
		this.groupsList = await api.getUserGroups(this.selectedTenantGroupId);
	}

	@Watch("selectedTenantGroupId")
	private async onSelectedTenantGroupIdChange(newSelectedTenantGroupId: any) {
		this.groupsList = await api.getUserGroups(newSelectedTenantGroupId);

		// update the prop of UserGroupRoles.selectedTenantGroupId in the modal
		this.groupModalItems[1].props.selectedTenantGroupId = newSelectedTenantGroupId;
	}

	private get modalSize() {
		return this.getFeature(['Users', 'LandingPage']) ? "xlg" : "lg"
	}

	private async SetupUserGroupEventDuressTypeConfiguration(domainsLoaded: boolean) {
		await this.loadServerEventTypes({
				serverType: serverTypeId_ManualRaise,
				groupID: this.selectedTenantGroupId,
			});

		if (this.eventTypes !== undefined) {
			this.duressServerEventTypes = this.eventTypes.filter((set: ServerEventType) => set.isDuressEvent === true);
			this.duressServerEventTypes.forEach(dset => dset.duressServerEventTypeTitle = dset.title);

			this.groupsList.forEach((userGroup: UserGroup) => {
				userGroup.duressServerEventTypeTitle = "";

				if (userGroup.duressServerTypeID !== null && userGroup.duressEventNum !== null) {
					let serverEventType = this.duressServerEventTypes.find((dset: ServerEventType) => dset.serverTypeID == userGroup.duressServerTypeID 
																								   && dset.eventNum == userGroup.duressEventNum);
					if (serverEventType !== undefined && serverEventType !== null) {
						userGroup.duressServerEventTypeTitle = serverEventType.title;			
					}
				}
			});
		}

		let order = domainsLoaded ? 4 : 3;
		this.groupColumns.push(
		{
			title: "Duress Event Type",
			key: "duressServerEventTypeTitle",
			order: order,
			sortOrder: 0,
			sortOrderReversed: false,
			description: "The duress Event Type name",
			searchable: true,
			visible: true,
			useCustomCell: true
		});
	}

	public async duressEventTypeSelected(duressEventType: ServerEventType, row: UserGroup) {	
		let userGroup = this.groupsList.find(ug => ug.userGroupId === row.userGroupId);
		if (userGroup !== null) {
			if (duressEventType !== null) {
				userGroup.duressServerTypeID = duressEventType.serverTypeID;
				userGroup.duressEventNum = duressEventType.eventNum;
				userGroup.duressServerEventTypeTitle = duressEventType.title;	
			} else {
				userGroup.duressServerTypeID = null;
				userGroup.duressEventNum = null
				userGroup.duressServerEventTypeTitle = null;	
			}

			await api.saveUserGroup(userGroup, this.selectedTenantGroupId);
		}
	}	
}
